import React from 'react';
import styled from 'styled-components';
import { BodyText, FlexColumn, FlexRow, SvgIcon } from '@make-software/cspr-design';
import UsersIcon from 'assets/icons/ic-users.svg';
import NewAppIcon from 'assets/icons/ic-sidebar-plus-icon.svg';
import AppIcon from 'assets/icons/ic-sidebar-app.svg';
import StatisticIcon from 'assets/icons/ic-sidebar-statistic.svg';
import ExploreCatalogIcon from 'assets/icons/ic-sidebar-explore.svg';
import { AssetsAliases } from 'app/assets';
import { generatePath, NavLink, useNavigate } from 'react-router-dom';
import {
  HOME_PATH,
  APPS_PATH,
  USERS_MANAGEMENT_PATH,
  APPS_CREATE_PATH,
  SIGN_IN_PATH,
  STATISTICS_PATH,
  MARKETPLACE_PATH
} from 'app/route/paths';
import { App, getAppsList } from 'app/console-api/apps/apps';
import { PaginationResponseType } from 'app/console-api';
import { AUTH_TOKEN, customErrorMessage, CustomEvents, statusCode } from 'app/constants';
import { HttpError } from 'app/components/utils/http-error';

interface SideBarProps {
  hide?: boolean;
}

const StyledSideBar = styled.div<{ hide?: boolean }>(({ theme, hide }) =>
  theme.withMedia({
    backgroundColor: theme.styleguideColors.backgroundTertiary,
    width: ['18%'],
    minHeight: '100vh',
    display: hide ? 'none' : ['none', 'block', 'block']
  })
);

const StyledLogoWrapper = styled.div(({ theme }) =>
  theme.withMedia({
    height: '72px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.styleguideColors.contentBlue,
    cursor: 'pointer'
  })
);

const NavItem = styled(NavLink)(({ theme }) =>
  theme.withMedia({
    color: theme.styleguideColors.contentQuaternary,
    display: 'flex',
    gap: '8px',
    fontSize: '16px',
    fontWeight: '500',
    lineHeight: '24px',
    alignItems: 'center',
    path: {
      fill: theme.styleguideColors.contentTertiary
    },
    '&:hover': {
      cursor: 'pointer',
      path: {
        fill: theme.styleguideColors.contentLightBlue
      },
      color: theme.styleguideColors.contentOnFill
    },
    // Active route (React Router adds aria-current="page")
    '&[aria-current="page"]': {
      color: theme.styleguideColors.contentOnFill,
      path: {
        fill: theme.styleguideColors.contentLightBlue
      }
    }
  })
);

const NavItemTitle = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    width: ['120px', '120px', '120px', '180px'],
    fontSize: '16px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  })
);

const NavItemContainer = styled(FlexColumn)(({ theme }) => ({
  padding: '0 32px',
  marginTop: '40px',
  gap: '40px'
}));

const StyledDivider = styled.div(({ theme }) =>
  theme.withMedia({
    marginLeft: '-34px',
    borderBottom: `1px solid ${theme.styleguideColors.contentSecondary}`,
    width: ['133%']
  })
);

const ApplicationWrapper = styled(FlexColumn)(({ theme }) => ({
  position: 'relative',
  gap: '24px'
}));

const StyledNewAppRow = styled(FlexRow)(({ theme }) => ({
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const StyledAddNew = styled(FlexRow)(({ theme }) => ({
  backgroundColor: '#1B254A',
  padding: '6px',
  cursor: 'pointer',
  borderRadius: '4px',
  ':hover': {
    backgroundColor: '#2e3a67'
  }
}));

const NavTitle = styled.div(({ theme }) => ({
  fontSize: '13px',
  fontWeight: '400',
  lineHeight: '16px',
  color: theme.styleguideColors.contentTertiary,
  textTransform: 'uppercase'
}));

export const Sidebar = ({ hide }: SideBarProps) => {
  const navigate = useNavigate();
  const handleHomeRedirect = () => {
    navigate(HOME_PATH);
  };

  const handleCreateTaskRedirect = () => {
    navigate(APPS_CREATE_PATH);
  };

  const [appsList, setAppsList] = React.useState<PaginationResponseType<App> | null>(null);

  const refreshApps = () => {
    getAppsList({ currentPage: 1, pageSize: Number(process.env.REACT_APP_PAGE_SIZE_ITEMS) })
      .then((response) => setAppsList(response))
      .catch((error) => {
        if (error instanceof HttpError && error.statusCode === statusCode.authentication_error) {
          localStorage.removeItem(AUTH_TOKEN);
          window.location.href = SIGN_IN_PATH;
          return;
        }
      });
  };

  React.useEffect(() => {
    refreshApps();
    window.addEventListener(CustomEvents.appCreated, refreshApps);
    window.addEventListener(CustomEvents.appUpdated, refreshApps);
    return () => {
      window.removeEventListener(CustomEvents.appCreated, refreshApps);
      window.removeEventListener(CustomEvents.appUpdated, refreshApps);
    };
  }, []);

  return (
    <StyledSideBar hide={hide}>
      <StyledLogoWrapper onClick={handleHomeRedirect}>
        <SvgIcon src={AssetsAliases.makeTrafficLogoWhite} height={40} width={220} />
      </StyledLogoWrapper>
      <NavItemContainer direction={'column'}>
        <FlexColumn gap={40}>
          <NavItem to={MARKETPLACE_PATH}>
            <SvgIcon src={ExploreCatalogIcon} size={16} />
            <NavItemTitle size={3}>Marketplace</NavItemTitle>
          </NavItem>

          <NavItem to={STATISTICS_PATH}>
            <SvgIcon src={StatisticIcon} size={16} />
            <NavItemTitle size={3}>Analytics</NavItemTitle>
          </NavItem>
          <StyledDivider></StyledDivider>
        </FlexColumn>
        <FlexColumn gap={40}>
          <StyledNewAppRow>
            <NavItem to={''}>
              <NavTitle>Applications</NavTitle>
            </NavItem>
            <StyledAddNew>
              <SvgIcon onClick={handleCreateTaskRedirect} src={NewAppIcon} size={16} />
            </StyledAddNew>
          </StyledNewAppRow>

          <ApplicationWrapper>
            {appsList &&
              appsList.data?.map((app) => (
                <NavItem
                  to={`${generatePath(APPS_PATH, { appID: app.id })}`}
                  key={app.id}
                  state={{ app: app }}>
                  <SvgIcon src={AppIcon} size={16} />
                  <NavItemTitle size={3}>{app.name}</NavItemTitle>
                </NavItem>
              ))}
          </ApplicationWrapper>
          <StyledDivider />
        </FlexColumn>
        <FlexRow gap={40} direction={'column'}>
          <NavItem to={USERS_MANAGEMENT_PATH}>
            <SvgIcon src={UsersIcon} size={16} />
            <NavItemTitle size={3}>Accounts</NavItemTitle>
          </NavItem>
        </FlexRow>
      </NavItemContainer>
    </StyledSideBar>
  );
};
